/**
 * Groups metricKeys into product-area categories for the admin Event Logs UI.
 *
 * Shared between backend (admin summary endpoint) and frontend (Clinic Detail page)
 * so category membership has exactly one source of truth.
 */
export type MetricCategory = 'schedule' | 'clinical' | 'billing' | 'patients' | 'setup' | 'support';

export const METRIC_CATEGORY_LABELS: Record<MetricCategory, string> = {
  schedule: 'Schedule',
  clinical: 'Clinical',
  billing: 'Billing',
  patients: 'Patients',
  setup: 'Setup',
  support: 'Support',
};

export const METRIC_CATEGORY_ORDER: MetricCategory[] = [
  'schedule',
  'clinical',
  'billing',
  'patients',
  'setup',
  'support',
];

/**
 * Human-readable labels for metrics. Falls back to title-cased key if not listed.
 */
export const METRIC_LABELS: Record<string, string> = {
  appointments_created: 'Appointments Created',
  appointments_checked_in: 'Checked In',
  appointments_checked_out: 'Checked Out',
  encounters_created: 'Encounters Started',
  encounters_signed: 'Encounters Signed',
  voice_to_chart_uses: 'Voice to Chart',
  pcp_initial_reports: 'PCP Initial',
  pcp_interim_reports: 'PCP Interim',
  pcp_release_reports: 'PCP Release',
  ccs_sent: 'CCS Sent',
  ccs_completed: 'CCS Completed',
  post_care_surveys_sent: 'Post-Care Sent',
  post_care_surveys_completed: 'Post-Care Completed',
  purchases_created: 'Purchases',
  claims_sent: 'Claims Sent',
  claims_accepted: 'Claims Accepted',
  payments_collected: 'Payments',
  eras_uploaded: 'ERAs Uploaded',
  npi_rejections: 'NPI Rejections',
  enrollment_rejections: 'Enrollment Rejections',
  format_837_rejections: '837 Format Rejections',
  provider_loop_errors: 'Provider Loop Errors',
  referring_provider_errors: 'Referring Provider Errors',
  condition_references_accessed: 'Condition DB Viewed',
  condition_references_edited: 'Condition DB Edited',
  clinical_evaluations_accessed: 'Evaluations Viewed',
  treatment_techniques_accessed: 'Treatments Viewed',
  marketing_campaigns_accessed: 'Marketing Viewed',
  forms_library_downloads: 'Forms Downloaded',
  research_reviews_accessed: 'Research Viewed',
  patients_created: 'Patients Added',
  condition_reports_sent: 'Condition Reports Sent',
  condition_reports_opened: 'Condition Reports Opened',
  google_review_links_sent: 'Google Review Links',
  clinic_logo_added: 'Clinic Logo Added',
  facebook_linked: 'Facebook Linked',
  google_review_url_added: 'Google Review URL',
  billing_info_added: 'Billing Info Added',
  commercial_numbers_added: 'Commercial Numbers',
  features_suggested: 'Feature Requests',
  features_voted: 'Feature Votes',
  knowledge_base_accessed: 'Knowledge Base',
  healthcom_logins: 'Patient Logins',
  active_users: 'Active Users',
  plan_changes: 'Plan Changes',
  user_role_changes: 'Role Changes',
  users_removed: 'Users Removed',
  users_added: 'Users Added',
  clinic_locations_added: 'Locations Added',
  clinic_schedule_updates: 'Clinic Schedule Updates',
  survey_preferences_updates: 'Survey Preferences Updates',
  scheduling_preferences_updates: 'Scheduling Preferences Updates',
  billing_global_settings_updates: 'Billing Settings Updates',
  provider_billing_updates: 'Provider Billing Updates',
  provider_schedule_updates: 'Provider Schedule Updates',
  provider_signature_updates: 'Provider Signature Updates',
  provider_profile_picture_updates: 'Provider Photo Updates',
  online_scheduling_url_updates: 'Online Schedule URL Updates',
  online_bookings_user_toggles: 'Online Booking Toggles',
  online_booking_treatment_updates: 'Online Booking Treatments',
  payors_added: 'Payors Added',
  billing_codes_added: 'Billing Codes Added',
  modalities_added: 'Modalities Added',
  supplements_added: 'Supplements Added',
  healthcare_providers_added: 'Healthcare Providers Added',
  tests_added: 'Tests Added',
  no_show_cancel_fees_added: 'No-Show Fees Added',
  no_show_cancel_fees_updated: 'No-Show Fees Updated',
  packages_added: 'Packages Added',
  packages_updated: 'Packages Updated',
  disciplines_added: 'Disciplines Added',
  disciplines_updated: 'Disciplines Updated',
  kiosk_connections: 'Kiosk Connections',
  kiosk_disconnections: 'Kiosk Disconnections',
};

/**
 * Category assignment for each metricKey.
 * Keep in sync with metricDefinitions.ts on the backend cron.
 */
export const METRIC_TO_CATEGORY: Record<string, MetricCategory> = {
  // Schedule
  appointments_created: 'schedule',
  appointments_checked_in: 'schedule',
  appointments_checked_out: 'schedule',
  clinic_schedule_updates: 'schedule',
  scheduling_preferences_updates: 'schedule',
  online_scheduling_url_updates: 'schedule',
  online_bookings_user_toggles: 'schedule',
  online_booking_treatment_updates: 'schedule',

  // Clinical
  encounters_created: 'clinical',
  encounters_signed: 'clinical',
  voice_to_chart_uses: 'clinical',
  pcp_initial_reports: 'clinical',
  pcp_interim_reports: 'clinical',
  pcp_release_reports: 'clinical',
  ccs_sent: 'clinical',
  ccs_completed: 'clinical',
  post_care_surveys_sent: 'clinical',
  post_care_surveys_completed: 'clinical',
  condition_references_accessed: 'clinical',
  condition_references_edited: 'clinical',
  clinical_evaluations_accessed: 'clinical',
  treatment_techniques_accessed: 'clinical',
  research_reviews_accessed: 'clinical',

  // Billing
  purchases_created: 'billing',
  claims_sent: 'billing',
  claims_accepted: 'billing',
  payments_collected: 'billing',
  eras_uploaded: 'billing',
  npi_rejections: 'billing',
  enrollment_rejections: 'billing',
  format_837_rejections: 'billing',
  provider_loop_errors: 'billing',
  referring_provider_errors: 'billing',
  billing_info_added: 'billing',
  commercial_numbers_added: 'billing',
  billing_global_settings_updates: 'billing',
  provider_billing_updates: 'billing',
  payors_added: 'billing',
  billing_codes_added: 'billing',
  no_show_cancel_fees_added: 'billing',
  no_show_cancel_fees_updated: 'billing',
  packages_added: 'billing',
  packages_updated: 'billing',

  // Patients
  patients_created: 'patients',
  condition_reports_sent: 'patients',
  condition_reports_opened: 'patients',
  google_review_links_sent: 'patients',
  healthcom_logins: 'patients',
  survey_preferences_updates: 'patients',
  marketing_campaigns_accessed: 'patients',

  // Setup
  clinic_logo_added: 'setup',
  facebook_linked: 'setup',
  google_review_url_added: 'setup',
  clinic_locations_added: 'setup',
  user_role_changes: 'setup',
  users_removed: 'setup',
  users_added: 'setup',
  plan_changes: 'setup',
  active_users: 'setup',
  provider_schedule_updates: 'setup',
  provider_signature_updates: 'setup',
  provider_profile_picture_updates: 'setup',
  modalities_added: 'setup',
  supplements_added: 'setup',
  healthcare_providers_added: 'setup',
  tests_added: 'setup',
  disciplines_added: 'setup',
  disciplines_updated: 'setup',
  kiosk_connections: 'setup',
  kiosk_disconnections: 'setup',

  // Support
  features_suggested: 'support',
  features_voted: 'support',
  knowledge_base_accessed: 'support',
  forms_library_downloads: 'support',
};

export const categoryForMetric = (metricKey: string): MetricCategory => {
  return METRIC_TO_CATEGORY[metricKey] ?? 'support';
};

export const labelForMetric = (metricKey: string): string => {
  if (METRIC_LABELS[metricKey]) return METRIC_LABELS[metricKey];

  return metricKey
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};
